import 'quill/dist/quill.snow.css';

import { type RichText } from '@fruitful/types';
import markdownToDelta from 'markdown-to-quill-delta';
import { Editor } from 'primereact/editor';
import type Quill from 'quill';
import { Delta } from 'quill';
import { type CSSProperties, useEffect, useState } from 'react';
import { View } from 'smoothie';

interface RichTextEditorProps {
    placeholder?: string;
    initial?: RichText;
    disabled?: boolean;
    height?: number | string;
    containerStyle?: CSSProperties;
    onChange: (s: RichText) => void;
}

/* Default Quill toolbar without image button */
const EDITOR_MODULES = {
    toolbar: [
        [{ header: [] }, { font: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['clean'],
    ],
    keyboard: {
        bindings: {
            // Grove does not expose table editing; Quill's default table arrow
            // handlers can throw when pasted table structure is incomplete.
            'table down': false,
            'table up': false,
        },
    },
};

/**
 * Rich Text is stored as either:
 *   1. Plain text (legacy notes)
 *   2. JSON Encoded string of Quill JS deltas
 *
 * This function unpacks/interprets that value
 */
const unpackRichText = (text: string | null | undefined): RichText => {
    if (text == null) return '';

    try {
        return JSON.parse(text) as RichText;
    } catch {
        return text ?? '';
    }
};

/**
 * Appends new RichText content to existing RichText content.
 * Handles all RichText formats (Delta, Op[], or string).
 */
function appendRichText(
    existing: RichText,
    newContent: RichText,
    insert: string = '\n\n',
): RichText {
    const existingDelta = new Delta(
        typeof existing === 'string' ? [{ insert: `${existing}\n` }] : existing,
    );
    const newDelta = new Delta(
        typeof newContent === 'string'
            ? [{ insert: `${newContent}\n` }]
            : newContent,
    );

    // Compose the new content after the existing content with a newline separator
    const separator = new Delta([{ insert }]);
    return existingDelta.compose(separator).compose(newDelta).ops;
}

const RichTextEditor = ({
    placeholder,
    initial,
    disabled = false,
    height,
    containerStyle,
    onChange,
}: RichTextEditorProps) => {
    const [quill, setQuill] = useState<Quill | null>(null);

    // Force Quill to update disabled state when prop changes
    useEffect(() => {
        if (quill) {
            quill.enable(!disabled);
        }
    }, [disabled, quill]);

    const init = (q: Quill) => {
        setQuill(q);

        if (initial !== undefined) {
            let deltaOrOps = initial;

            /**
             * The original notes feature stored all notes as plain text in
             * the database. In order to be backwards compatible,
             * we convert any string notes to a single op that we can pass
             * to the editor
             */
            if (typeof deltaOrOps === 'string')
                deltaOrOps = [{ insert: `${deltaOrOps}\n` }];

            q.setContents(deltaOrOps);
        }
    };

    /* Wrap the onChange event since PrimeReact/Editor only emits HTML */
    const emitDelta = () => {
        const contents = quill?.getContents();
        if (contents) onChange(contents);
    };

    return (
        <View style={containerStyle}>
            <style>
                {`
                /* Revert tailwind's display property overrides */
                .rtf-editor .ql-editor * {     
                    display: revert;
                }

                /* https://github.com/slab/quill/issues/2614 */
                .rtf-editor .ql-editor li[data-list="bullet"]::before {
                    content: '';
                }
            `}
            </style>
            <Editor
                onLoad={init}
                onTextChange={emitDelta}
                modules={EDITOR_MODULES}
                showHeader={false}
                placeholder={placeholder}
                readOnly={disabled}
                className="rtf-editor"
                style={{
                    height,
                    margin: '16 0',
                }}
            />
        </View>
    );
};

export { appendRichText, markdownToDelta, RichTextEditor, unpackRichText };
